import { validateInputProjection } from "@/projection";
import type { UploadFetch } from "@/upload";

type InspectionResponse = { crs: string | null; crs_source: "header" | "prj" | null; warnings: readonly string[] };

export type CrsInspection =
    { kind: "inspecting" } | ({ kind: "complete" } & InspectionResponse) | { kind: "failed"; message: string };

type Inspectable = { file: File; inspection: CrsInspection | null };

const SAMPLE_BYTES = 32 * 1024;
const CONCURRENCY = 3;
const INSPECTION_TIMEOUT_MS = 30_000;
const inspections = new WeakMap<Inspectable, { controller: AbortController; prj: File | null }>();
const queue: Array<() => Promise<void>> = [];
let active = 0;

/** Returns null while any inspection is still running. */
export function sharedInspectionEpsg(uploads: readonly { inspection?: CrsInspection | null }[]): string | null {
    if (uploads.some((upload) => upload.inspection?.kind === "inspecting")) return null;
    const first = uploads[0]?.inspection;
    if (first?.kind !== "complete" || !first.crs || !validateInputProjection(first.crs)) return "";
    return uploads.every((upload) => upload.inspection?.kind === "complete" && upload.inspection.crs === first.crs)
        ? first.crs
        : "";
}

export function mixedInspections(uploads: readonly { inspection?: CrsInspection | null }[]): boolean {
    const inspections = uploads.flatMap((upload) => (upload.inspection ? [upload.inspection] : []));
    return (
        inspections.every((inspection) => inspection.kind !== "inspecting") &&
        new Set(inspections.map((inspection) => (inspection.kind === "complete" ? inspection.crs : null))).size > 1
    );
}

export function cancelCrsInspection(upload: Inspectable) {
    inspections.get(upload)?.controller.abort();
    inspections.delete(upload);
}

export function startCrsInspection(upload: Inspectable, onUpdate: () => void, prj: File | null, request: UploadFetch) {
    if (inspections.get(upload)?.prj === prj) return;
    cancelCrsInspection(upload);
    const controller = new AbortController();
    inspections.set(upload, { controller, prj });
    upload.inspection = { kind: "inspecting" };
    queue.push(async () => {
        if (controller.signal.aborted) return;
        const result = await inspectFileCrs(upload.file, prj, controller.signal, request).catch(
            (error): CrsInspection => ({
                kind: "failed",
                message: error instanceof Error ? error.message : "CRS inspection failed",
            }),
        );
        if (controller.signal.aborted) return;
        upload.inspection = result;
        onUpdate();
    });
    drain();
}

function drain() {
    while (active < CONCURRENCY) {
        const job = queue.shift();
        if (!job) return;
        active++;
        void job().finally(() => {
            active--;
            drain();
        });
    }
}

async function inspectFileCrs(
    file: File,
    prj: File | null,
    signal: AbortSignal,
    request: UploadFetch,
): Promise<CrsInspection> {
    const head = Math.min(file.size, SAMPLE_BYTES);
    const body = new FormData();
    body.append("size", String(file.size));
    body.append("head", file.slice(0, head));
    body.append("tail", file.slice(Math.max(head, file.size - SAMPLE_BYTES)));
    if (prj) body.append("prj", await prj.text());
    const response = await request("/api/viewer/detect-crs", {
        method: "POST",
        body,
        signal: AbortSignal.any([signal, AbortSignal.timeout(INSPECTION_TIMEOUT_MS)]),
    });
    if (!response.ok) {
        const error = (await response.json().catch(() => null)) as { reason?: string } | null;
        throw new Error(error?.reason ?? `CRS inspection failed (${response.status})`);
    }
    return { kind: "complete", ...((await response.json()) as InspectionResponse) };
}
